import { Lead } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  endBefore,
  getDocs,
  limit,
  orderBy,
  Query,
  query,
  QueryDocumentSnapshot,
  startAfter,
} from 'firebase/firestore';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardContext } from '../contexts/DashboardContext';
import Button from './Common/Button';
import Tooltip from './Common/Tooltip';
import { CustomDataGrid } from './CustomDataGrid/CustomDataGrid';

const PAGE_SIZE = 25;

interface Props {
  query: Query<Lead>;
}

const fetchPaginatedDocs = async function <T>(
  fQuery: Query<T>,
  pageParam: QueryDocumentSnapshot<T> | null,
  direction: 'next' | 'previous' = 'next',
) {
  const collectionRef = fQuery;
  let q;

  const order = orderBy('createdAt', 'desc');

  if (direction === 'next' && pageParam) {
    q = query(collectionRef, order, startAfter(pageParam), limit(PAGE_SIZE));
  } else if (direction === 'previous' && pageParam) {
    q = query(collectionRef, order, endBefore(pageParam), limit(PAGE_SIZE));
  } else {
    q = query(collectionRef, order, limit(PAGE_SIZE));
  }

  const snapshot = await getDocs(q);
  const docs: QueryDocumentSnapshot<T>[] = snapshot.docs;

  return docs;
};

export const LeadsTable = ({ query }: Props) => {
  const navigate = useNavigate();
  const { dynamicPages, forms } = useDashboardContext();

  const {
    // status,
    data,
    // error,
    isFetching,
    // isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['leads', query],
    queryFn: (ctx) =>
      fetchPaginatedDocs(query, ctx.pageParam).catch((e) => {
        Sentry.captureException(e);
        throw e;
      }),
    getNextPageParam: (group) => group[group.length - 1],
    initialPageParam: null as QueryDocumentSnapshot<Lead> | null,
    refetchOnWindowFocus: false,
  });

  const columns: ColumnDef<QueryDocumentSnapshot<Lead>>[] = useMemo(() => {
    return [
      {
        id: 'createdAt',
        header: 'Timestamp',
        accessorFn: (row) => row.data().createdAt?.toDate(),
        size: 200,
        cell: (info) => info.getValue()?.toLocaleString(),
      },
      {
        id: 'id',
        header: 'Identifier',
        accessorFn: (row) => row.id,
        size: 250,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <div>
              <div className="font-medium text-gray-900">
                {
                  Object.values(leadData.form).find(
                    ({ label }) => label?.toLowerCase() === 'email',
                  )?.value
                }
              </div>
              <div className="mt-1 text-gray-500">{info.row.original.id}</div>
            </div>
          );
        },
      },
      {
        id: 'data',
        header: 'Data',
        accessorFn: (row) => row.data().form,
        size: 300,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Tooltip
              tooltipClassName="whitespace-pre-wrap"
              text={Object.entries(leadData.form)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([_, { label, value }]) => `${label}: ${value}`)
                .join('\n')}
            >
              {Object.entries(leadData.form)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([_, { value }]) => `${value}`)
                .join(', ')}
            </Tooltip>
          );
        },
      },
      {
        id: 'url',
        header: 'Url',
        accessorFn: (row) => row.data().url,
        size: 200,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Tooltip
              text={leadData.url}
              wrapperClassName="max-w-full truncate"
              tooltipClassName="break-all"
            >
              {leadData.url.trim()}
            </Tooltip>
          );
        },
      },
      {
        id: 'dynamicPage',
        header: 'Dynamic Page',
        accessorFn: (row) => row.data().campaignId,
        size: 180,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Button
              variant="ternary"
              onClick={() => {
                navigate(`../pages/${leadData.campaignId}/details`);
              }}
              className="font-normal text-blue-500 underline"
            >
              {dynamicPages?.find((d) => d.id === leadData.campaignId)?.data()
                .name ?? leadData.campaignId.slice(-5)}
            </Button>
          );
        },
      },
      {
        id: 'form',
        header: 'Form',
        accessorFn: (row) => row.data().formId,
        size: 150,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Button
              variant="ternary"
              onClick={() => {
                navigate('../forms');
              }}
              className="font-normal text-blue-500 underline"
            >
              {forms?.find((f) => f.id === leadData.formId)?.data().name ??
                leadData.formId.slice(-5)}
            </Button>
          );
        },
      },
      {
        id: 'variant',
        header: 'Variant',
        accessorFn: (row) => row.data().websiteId,
        size: 150,
        cell: (info) => {
          const leadData = info.row.original.data();
          return (
            <Button
              variant="ternary"
              onClick={() => {
                navigate(
                  `../pages/${leadData.campaignId}?tab=details&variantId=${leadData.websiteId}`,
                );
              }}
              className="font-normal text-blue-500 underline"
            >
              {leadData.variantId.slice(-5)}
            </Button>
          );
        },
      },
    ];
  }, [dynamicPages, navigate, forms]);

  return (
    <CustomDataGrid
      logicalName="leads"
      columns={columns}
      data={data?.pages.flatMap((p) => p)}
      onReachedEnd={() => !isFetching && hasNextPage && fetchNextPage()}
      rowClassName="min-h-20"
      initialState={{
        columnVisibility: {
          id: false,
        },
      }}
    />
  );
};
